import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Upload, 
  message, 
  Row, 
  Col, 
  InputNumber,
  Switch,
  Divider,
  Typography,
  Space,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  UploadOutlined, 
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { 
  createInventoryItem,
  updateInventoryItem,
  bulkImportInventory,
  processSalesTransaction,
  MATERIAL_TYPES,
  INVENTORY_CONDITION,
  CONDITION_LABELS,
  CONDITION_COLORS
} from '../../firebase/inventoryService';
import './InventoryForm.css';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Dragger } = Upload;

const InventoryForm = ({ 
  visible, 
  onCancel, 
  onSuccess, 
  editingItem = null,
  mode = 'single' // 'single', 'bulk', 'csv'
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [bulkItems, setBulkItems] = useState([{ key: 1 }]);

  // Initialize form when editing
  useEffect(() => {
    if (editingItem && visible) {
      form.setFieldsValue({
        ...editingItem,
        purchaseDate: editingItem.purchaseDate ? dayjs(editingItem.purchaseDate) : null,
        lastMaintenanceDate: editingItem.lastMaintenanceDate ? dayjs(editingItem.lastMaintenanceDate) : null
      });
    } else if (visible && mode === 'single') {
      form.resetFields();
      // Set default values
      form.setFieldsValue({
        newQuantity: 0,
        usedQuantity: 0,
        damagedQuantity: 0,
        totalQuantity: 0
      });
    }
  }, [editingItem, visible, form, mode]);

  // Auto-calculate total quantity
  useEffect(() => {
    const newQty = form.getFieldValue('newQuantity') || 0;
    const usedQty = form.getFieldValue('usedQuantity') || 0;
    const damagedQty = form.getFieldValue('damagedQuantity') || 0;
    const total = newQty + usedQty + damagedQty;
    
    form.setFieldValue('totalQuantity', total);
  }, [form]);

  // Handle quantity field changes
  const handleQuantityChange = () => {
    const newQty = form.getFieldValue('newQuantity') || 0;
    const usedQty = form.getFieldValue('usedQuantity') || 0;
    const damagedQty = form.getFieldValue('damagedQuantity') || 0;
    const total = newQty + usedQty + damagedQty;
    
    form.setFieldValue('totalQuantity', total);
  };

  // Handle single item submission
  const handleSingleSubmit = async (values) => {
    setLoading(true);
    try {
      const itemData = {
        materialType: values.materialType,
        totalQuantity: values.totalQuantity || 0,
        newQuantity: values.newQuantity || 0,
        usedQuantity: values.usedQuantity || 0,
        damagedQuantity: values.damagedQuantity || 0,
        notes: values.notes || '',
        purchaseDate: values.purchaseDate ? values.purchaseDate.toDate() : null,
        lastMaintenanceDate: values.lastMaintenanceDate ? values.lastMaintenanceDate.toDate() : null
      };

      if (editingItem) {
        await updateInventoryItem(editingItem.id, itemData);
        message.success('Inventory item updated successfully');
      } else {
        await createInventoryItem(itemData);
        message.success('Inventory quantities added successfully');
      }

      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      message.error('Failed to save inventory item');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk submission
  const handleBulkSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      
      // Process each bulk item as a quantity-based inventory addition
      for (const item of bulkItems) {
        const materialType = values[`materialType_${item.key}`];
        const quantity = values[`quantity_${item.key}`] || 0;
        const notes = values[`notes_${item.key}`] || '';
        const purchaseDate = values[`purchaseDate_${item.key}`] ? values[`purchaseDate_${item.key}`].toDate() : null;

        if (materialType && quantity > 0) {
          const itemData = {
            materialType,
            totalQuantity: quantity,
            newQuantity: quantity, // Default new items to "new" status
            usedQuantity: 0,
            damagedQuantity: 0,
            notes,
            purchaseDate
          };

          await createInventoryItem(itemData);
        }
      }

      message.success(`Successfully added ${bulkItems.length} inventory items`);
      form.resetFields();
      setBulkItems([{ key: 1 }]);
      onSuccess();
    } catch (error) {
      console.error('Error bulk saving inventory items:', error);
      message.error('Failed to save inventory items');
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV upload
  const handleCsvUpload = async () => {
    if (!csvData.length) {
      message.error('Please upload a CSV file first');
      return;
    }

    setLoading(true);
    try {
      // Process CSV data for quantity-based system
      for (const csvItem of csvData) {
        if (csvItem.materialType && csvItem.quantity > 0) {
          const itemData = {
            materialType: csvItem.materialType,
            totalQuantity: csvItem.quantity,
            newQuantity: csvItem.quantity, // Default to "new" status
            usedQuantity: 0,
            damagedQuantity: 0,
            notes: csvItem.notes || '',
            purchaseDate: csvItem.purchaseDate
          };

          await createInventoryItem(itemData);
        }
      }

      message.success(`Successfully imported ${csvData.length} inventory items`);
      setCsvData([]);
      setPreviewData([]);
      setUploadedFile(null);
      onSuccess();
    } catch (error) {
      console.error('Error importing CSV:', error);
      message.error('Failed to import CSV data');
    } finally {
      setLoading(false);
    }
  };


  // Add bulk item row
  const addBulkItem = () => {
    const newKey = Math.max(...bulkItems.map(item => item.key)) + 1;
    setBulkItems([...bulkItems, { key: newKey }]);
  };

  // Remove bulk item row
  const removeBulkItem = (key) => {
    if (bulkItems.length > 1) {
      setBulkItems(bulkItems.filter(item => item.key !== key));
    }
  };

  // Handle CSV file upload
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        const data = lines.slice(1)
          .filter(line => line.trim())
          .map(line => {
            const values = line.split(',').map(v => v.trim());
            const item = {};
            
            headers.forEach((header, index) => {
              const value = values[index] || '';
              
              switch (header.toLowerCase()) {
                case 'materialtype':
                case 'material_type':
                case 'material type':
                  item.materialType = value;
                  break;
                case 'quantity':
                  item.quantity = parseInt(value) || 0;
                  break;
                case 'notes':
                  item.notes = value;
                  break;
                case 'purchasedate':
                case 'purchase_date':
                case 'purchase date':
                  item.purchaseDate = value ? new Date(value) : null;
                  break;
                default:
                  // Handle any other columns
                  item[header] = value;
              }
            });
            
            // Set defaults for quantity-based system
            if (!item.purchaseDate) {
              item.purchaseDate = new Date();
            }
            
            return item;
          })
          .filter(item => item.materialType && item.quantity > 0); // Only include valid items

        setCsvData(data);
        setPreviewData(data.slice(0, 5)); // Show first 5 rows for preview
        message.success(`Parsed ${data.length} items from CSV`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        message.error('Failed to parse CSV file');
      }
    };
    
    reader.readAsText(file);
    setUploadedFile(file);
    return false; // Prevent default upload
  };

  // Render single item form
  const renderSingleForm = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSingleSubmit}
      className="inventory-form"
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="materialType"
            label="Material Type"
            rules={[{ required: true, message: 'Please select material type' }]}
          >
            <Select
              placeholder="Select material type"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {Object.values(MATERIAL_TYPES).sort().map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider>Quantities by Status</Divider>

      <Row gutter={16}>
        <Col span={6}>
          <Form.Item
            name="newQuantity"
            label="New Items"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber 
              min={0} 
              max={10000} 
              style={{ width: '100%' }} 
              placeholder="0"
              onChange={handleQuantityChange}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="usedQuantity"
            label="Used Items"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber 
              min={0} 
              max={10000} 
              style={{ width: '100%' }} 
              placeholder="0"
              onChange={handleQuantityChange}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="damagedQuantity"
            label="Damaged Items"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber 
              min={0} 
              max={10000} 
              style={{ width: '100%' }} 
              placeholder="0"
              onChange={handleQuantityChange}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="totalQuantity"
            label="Total Quantity"
            tooltip="Will be calculated automatically"
          >
            <InputNumber 
              disabled
              style={{ width: '100%' }} 
              placeholder="Auto-calculated"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="purchaseDate"
            label="Purchase Date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="lastMaintenanceDate"
            label="Last Maintenance Date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="notes"
        label="Notes"
      >
        <TextArea rows={3} placeholder="Enter any notes or comments" />
      </Form.Item>
    </Form>
  );

  // Render bulk form
  const renderBulkForm = () => (
    <Form
      form={form}
      layout="vertical"
      className="inventory-bulk-form"
    >
      <div className="bulk-items-container">
        {bulkItems.map((item, index) => (
          <div key={item.key} className="bulk-item-row">
            <Row gutter={8} align="middle">
              <Col span={8}>
                <Form.Item
                  name={`materialType_${item.key}`}
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select placeholder="Material Type" size="small">
                    {Object.values(MATERIAL_TYPES).sort().map(type => (
                      <Option key={type} value={type}>{type}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item 
                  name={`quantity_${item.key}`}
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber placeholder="Quantity" size="small" min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name={`purchaseDate_${item.key}`}>
                  <DatePicker size="small" style={{ width: '100%' }} placeholder="Purchase Date" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={`notes_${item.key}`}>
                  <Input placeholder="Notes" size="small" />
                </Form.Item>
              </Col>
              <Col span={1}>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeBulkItem(item.key)}
                  disabled={bulkItems.length === 1}
                />
              </Col>
            </Row>
          </div>
        ))}
      </div>
      
      <Button
        type="dashed"
        onClick={addBulkItem}
        icon={<PlusOutlined />}
        style={{ width: '100%', marginTop: 16 }}
      >
        Add Item
      </Button>
    </Form>
  );

  // Render CSV upload form
  const renderCsvForm = () => (
    <div className="csv-upload-container">
      <Alert
        message="CSV Format Requirements"
        description="CSV should contain columns: materialType, quantity, notes, purchaseDate. Quantities will be added as 'new' items by default."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Dragger
        accept=".csv"
        beforeUpload={handleFileUpload}
        fileList={uploadedFile ? [uploadedFile] : []}
        onRemove={() => {
          setUploadedFile(null);
          setCsvData([]);
          setPreviewData([]);
        }}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
        <p className="ant-upload-hint">
          Support for single CSV file upload. File will be parsed and validated before import.
        </p>
      </Dragger>

      {previewData.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Preview (First 5 rows)</Title>
          <div className="csv-preview">
            {previewData.map((item, index) => (
              <div key={index} className="preview-item">
                <Text strong>{item.materialType}</Text> - 
                <Text type="secondary">Quantity: {item.quantity || 'N/A'}</Text>
                {item.notes && <Text> - {item.notes}</Text>}
              </div>
            ))}
          </div>
          <Text type="secondary">
            Total items to import: {csvData.length}
          </Text>
        </div>
      )}
    </div>
  );

  // Render footer buttons
  const renderFooter = () => {
    const buttons = [
      <Button key="cancel" onClick={onCancel}>
        Cancel
      </Button>
    ];

    if (mode === 'single') {
      buttons.push(
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
          icon={<SaveOutlined />}
        >
          {editingItem ? 'Update' : 'Add'} Item
        </Button>
      );
    } else if (mode === 'bulk') {
      buttons.push(
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleBulkSubmit}
          icon={<SaveOutlined />}
        >
          Add All Items
        </Button>
      );
    } else if (mode === 'csv') {
      buttons.push(
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleCsvUpload}
          disabled={!csvData.length}
          icon={<UploadOutlined />}
        >
          Import CSV Data
        </Button>
      );
    }

    return buttons;
  };

  const getTitle = () => {
    if (editingItem) return 'Edit Inventory Item';
    if (mode === 'bulk') return 'Add Multiple Items';
    if (mode === 'csv') return 'Import from CSV';
    return 'Add Inventory Item';
  };

  return (
    <Modal
      title={getTitle()}
      open={visible}
      onCancel={onCancel}
      footer={renderFooter()}
      width={mode === 'single' ? 600 : mode === 'bulk' ? 900 : 700}
      className="inventory-form-modal"
      destroyOnClose
    >
      {mode === 'single' && renderSingleForm()}
      {mode === 'bulk' && renderBulkForm()}
      {mode === 'csv' && renderCsvForm()}
    </Modal>
  );
};

export default InventoryForm;
